// The editor's live preview: the article through the same renderer and
// stylesheets Publish uses (@vortex/help-render), at Scout, phone or desktop
// size, in Day or Night. A sandboxed frame with no scripts; allow-same-origin
// only so the article's images load with the session cookie, as on the page.

import { useEffect, useMemo, useRef, useState } from 'react';
import { renderArticle, SCOUT_CSS, WEB_CSS, type ArticleContent, type ArticleRef, type MediaInfo } from '@vortex/help-render';

type Size = 'scout' | 'phone' | 'desktop';
type Light = 'day' | 'night';

/** The Scout's article area: the 1280 × 800 screen less Help's 300 list of sections. */
const FRAMES: Record<Size, { w: number; h: number; label: string }> = {
  scout: { w: 980, h: 800, label: 'Scout' },
  phone: { w: 390, h: 844, label: 'Phone' },
  desktop: { w: 1280, h: 800, label: 'Desktop' },
};

const FONTS = {
  scout: 'https://fonts.googleapis.com/css2?family=Saira:wght@400;500;600&family=Azeret+Mono:wght@400;500&display=swap',
  web: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700&display=swap',
};

const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

export function previewDocument(article: ArticleContent, size: Size, light: Light, media: Record<string, MediaInfo>, articles: Record<string, ArticleRef>): string {
  const scout = size === 'scout';
  const theme = scout ? light : light === 'night' ? 'dark' : 'light';
  const body = renderArticle(article, { target: scout ? 'scout' : 'web', media, articles });
  return `<!doctype html><html lang="en" data-theme="${theme}"><head><meta charset="utf-8">`
    + `<link rel="stylesheet" href="${escapeAttr(scout ? FONTS.scout : FONTS.web)}">`
    + `<style>${scout ? SCOUT_CSS : WEB_CSS}</style></head><body>${body}</body></html>`;
}

const tab = (on: boolean) => `min-h-[40px] rounded-[7px] px-3 text-[15px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${on ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`;

interface Props {
  article: ArticleContent;
  media: Record<string, MediaInfo>;
  articles: Record<string, ArticleRef>;
}

export function HelpPreview({ article, media, articles }: Props) {
  const [size, setSize] = useState<Size>('scout');
  const [light, setLight] = useState<Light>('day');
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const frame = FRAMES[size];
  const scale = width ? Math.min(1, width / frame.w) : 1;
  const doc = useMemo(() => previewDocument(article, size, light, media, articles), [article, size, light, media, articles]);

  return (
    <section aria-label="Preview" className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div role="tablist" aria-label="Size" className="flex gap-0.5 rounded-[10px] bg-slate-400/10 p-[3px]">
          {(Object.keys(FRAMES) as Size[]).map((s) => (
            <button key={s} type="button" role="tab" aria-selected={size === s} className={tab(size === s)} onClick={() => setSize(s)}>{FRAMES[s].label}</button>
          ))}
        </div>
        <div role="tablist" aria-label="Light" className="flex gap-0.5 rounded-[10px] bg-slate-400/10 p-[3px]">
          <button type="button" role="tab" aria-selected={light === 'day'} className={tab(light === 'day')} onClick={() => setLight('day')}>Day</button>
          <button type="button" role="tab" aria-selected={light === 'night'} className={tab(light === 'night')} onClick={() => setLight('night')}>{size === 'scout' ? 'Night' : 'Dark'}</button>
        </div>
        <span className="text-[15px] text-slate-400">Same renderer as Publish · {frame.w} × {frame.h}</span>
      </div>
      <div ref={box} className="w-full">
        <div className="overflow-hidden rounded-xl border border-slate-400/20" style={{ width: frame.w * scale, height: frame.h * scale }}>
          <iframe
            title={`Preview, ${frame.label}, ${light === 'day' ? 'Day' : size === 'scout' ? 'Night' : 'Dark'}`}
            sandbox="allow-same-origin"
            srcDoc={doc}
            style={{ width: frame.w, height: frame.h, transform: `scale(${scale})`, transformOrigin: '0 0', border: 0, display: 'block' }}
          />
        </div>
      </div>
    </section>
  );
}
